/**
 * services/platform/platformApi.ts
 * The integration layer between platform screens and skplatform-api. Screens
 * never build URLs or call axios directly — they call these functions, which
 * read endpoints from api-endpoints/ and go through the shared axios instance
 * (httpOnly cookie session + silent refresh).
 *
 * Each function returns the unwrapped `data` payload. Screens wrap calls in
 * try/catch and fall back to the seeded working copy when a route isn't live
 * yet (the console stays fully navigable in dev).
 */

import axiosInstance from '../../api-endpoints/axiosInstance';
import * as EP from '../../api-endpoints/api-endpoints';
import type {
  ApiEnvelope, Org, UpdateOrgPayload, OrgMember, OrgInvitation, CreateInvitationPayload,
  Plan, Subscription, UsageResponse, PaymentMethod, Invoice, MfaEnrollResponse,
  RecoveryCodesResponse, AuditEvent, AdminOrgSummary, OrgHost, OutboxDeadLetter,
  ResidencyZone, SigningKeyRow, RlsBypassRow, RlsBypassSiteCount,
  RealmMismatchRow, AppAudienceRow, CustomerActivityPeriodRow, CustomerAccessRow,
} from '../../schema/platform/types';

const unwrap = <T,>(d: any): T => (d && typeof d === 'object' && 'data' in d ? d.data : d);

/* ── Organization ── */
export async function getOrg(orgId: string): Promise<Org> {
  const { data } = await axiosInstance.get<ApiEnvelope<Org>>(EP.Platform_Org_Url(orgId));
  return unwrap<Org>(data);
}
export async function updateOrg(orgId: string, payload: UpdateOrgPayload): Promise<Org> {
  const { data } = await axiosInstance.patch<ApiEnvelope<Org>>(EP.Platform_Org_Url(orgId), payload);
  return unwrap<Org>(data);
}

/* ── Team & invitations ── */
export async function listMembers(orgId: string): Promise<OrgMember[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<OrgMember[]>>(EP.Platform_Org_Members_Url(orgId));
  return unwrap<OrgMember[]>(data);
}
export async function grantRole(orgId: string, userId: string, roleId: string) {
  const { data } = await axiosInstance.post(EP.Platform_Org_Member_Roles_Url(orgId, userId), { role_id: roleId });
  return unwrap(data);
}
export async function removeMember(orgId: string, userId: string) {
  await axiosInstance.delete(EP.Platform_Org_Member_Url(orgId, userId));
}
export async function listInvitations(orgId: string): Promise<OrgInvitation[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<OrgInvitation[]>>(EP.Platform_Org_Invitations_Url(orgId));
  return unwrap<OrgInvitation[]>(data);
}
export async function createInvitation(orgId: string, payload: CreateInvitationPayload): Promise<OrgInvitation> {
  const { data } = await axiosInstance.post<ApiEnvelope<OrgInvitation>>(EP.Platform_Org_Invitations_Url(orgId), payload);
  return unwrap<OrgInvitation>(data);
}
export async function resendInvitation(orgId: string, invId: string) {
  await axiosInstance.post(EP.Platform_Org_Invitation_Resend_Url(orgId, invId));
}
export async function cancelInvitation(orgId: string, invId: string) {
  await axiosInstance.delete(EP.Platform_Org_Invitation_Url(orgId, invId));
}

/* ── Licensing ── */
export async function listPlans(): Promise<Plan[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<Plan[]>>(EP.Platform_Plans_Url);
  return unwrap<Plan[]>(data);
}
export async function getSubscription(orgId: string): Promise<Subscription> {
  const { data } = await axiosInstance.get<ApiEnvelope<Subscription>>(EP.Platform_Org_Subscription_Url(orgId));
  return unwrap<Subscription>(data);
}
export async function changePlan(orgId: string, subId: string, planId: string) {
  const { data } = await axiosInstance.post(EP.Platform_Org_Subscription_ChangePlan_Url(orgId, subId), { plan_id: planId });
  return unwrap(data);
}
export async function cancelSubscription(orgId: string) {
  await axiosInstance.post(EP.Platform_Org_Subscription_Cancel_Url(orgId));
}
export async function getUsage(orgId: string): Promise<UsageResponse> {
  const { data } = await axiosInstance.get<ApiEnvelope<UsageResponse>>(EP.Platform_Org_Usage_Url(orgId));
  return unwrap<UsageResponse>(data);
}

/* ── Billing ── */
export async function listPaymentMethods(orgId: string): Promise<PaymentMethod[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<PaymentMethod[]>>(EP.Platform_Org_PaymentMethods_Url(orgId));
  return unwrap<PaymentMethod[]>(data);
}
export async function addPaymentMethod(orgId: string): Promise<{ form_url: string }> {
  const { data } = await axiosInstance.post<ApiEnvelope<{ form_url: string }>>(EP.Platform_Org_PaymentMethods_Url(orgId), { method_type: 'card' });
  return unwrap<{ form_url: string }>(data);
}
export async function listInvoices(orgId: string): Promise<Invoice[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<Invoice[]>>(EP.Platform_Org_Invoices_Url(orgId));
  return unwrap<Invoice[]>(data);
}

/* ── Security (MFA) ── */
export async function enrollMfa(): Promise<MfaEnrollResponse> {
  const { data } = await axiosInstance.post<ApiEnvelope<MfaEnrollResponse>>(EP.Platform_Mfa_Enroll_Url);
  return unwrap<MfaEnrollResponse>(data);
}
export async function verifyMfaEnrollment(code: string): Promise<RecoveryCodesResponse> {
  const { data } = await axiosInstance.post<ApiEnvelope<RecoveryCodesResponse>>(EP.Platform_Mfa_Verify_Enroll_Url, { code });
  return unwrap<RecoveryCodesResponse>(data);
}
export async function disableMfa(code: string) {
  await axiosInstance.post(EP.Platform_Mfa_Disable_Url, { code });
}

/* ── Audit ── */
export async function getAuditLog(params?: Record<string, string>): Promise<AuditEvent[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<AuditEvent[]>>(EP.Platform_Audit_Log_Url, { params });
  return unwrap<AuditEvent[]>(data);
}

/* ── Admin ── */
export async function adminListOrgs(query?: string): Promise<AdminOrgSummary[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<AdminOrgSummary[]>>(EP.Platform_Admin_Orgs_Url, { params: query ? { query } : undefined });
  return unwrap<AdminOrgSummary[]>(data);
}

/* ── Admin — flagged-gap stubs (routes to be confirmed; see api-endpoints) ── */
export async function adminListApplications(): Promise<any[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<any[]>>(EP.Platform_Admin_Applications_Url);
  return unwrap<any[]>(data);
}
export async function adminListPlans(): Promise<any[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<any[]>>(EP.Platform_Admin_Plans_Url);
  return unwrap<any[]>(data);
}

/* ── Domains (CH-U1) ──────────────────────────────────────────────────────
   `listHosts` defaults to ACTIVE only. History is opt-in, matching the API and
   matching CH-U1's default filter — a panel that opened showing every domain
   the org ever held would bury the ones actually routing traffic. */
export async function listOrgHosts(orgId: string, includeInactive = false): Promise<OrgHost[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<OrgHost[]>>(
    EP.Platform_Org_Hosts_Url(orgId),
    includeInactive ? { params: { include_inactive: 'true' } } : undefined,
  );
  return unwrap<OrgHost[]>(data);
}

export async function bindOrgHost(orgId: string, host: string): Promise<OrgHost> {
  const { data } = await axiosInstance.post<ApiEnvelope<OrgHost>>(
    EP.Platform_Org_Hosts_Url(orgId),
    { host },
  );
  return unwrap<OrgHost>(data);
}

/**
 * Unbind a domain. DELETE is the verb; the effect is a DEACTIVATION — the row
 * survives and still appears under `include_inactive`. Nothing here removes a
 * row, which is why the UI says "Deactivate" and offers no delete.
 */
export async function deactivateOrgHost(orgId: string, hostId: string): Promise<void> {
  await axiosInstance.delete(EP.Platform_Org_Host_Url(orgId, hostId));
}

/* ── Outbox dead letters (CH-U5) ─────────────────────────────────────────── */
export async function listOutboxDeadLetters(limit = 50): Promise<OutboxDeadLetter[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<OutboxDeadLetter[]>>(
    EP.Platform_Outbox_Dlq_Url,
    { params: { limit } },
  );
  return unwrap<OutboxDeadLetter[]>(data);
}

export async function requeueOutboxEvent(id: string): Promise<void> {
  await axiosInstance.post(EP.Platform_Outbox_Requeue_Url(id));
}

/* ── SKPLATFORM-CC-006 / UICC-006 — residency, keys, ledgers, customer access ──
   Every function returns the unwrapped payload and lets the caller decide what
   an empty or failed read means. The two ledger reads in particular are
   EXPECTED to come back empty until the reporting role exists (see the note in
   api-endpoints.tsx), so the screens treat empty as a state rather than as an
   error. */

export async function listResidencyZones(): Promise<ResidencyZone[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<ResidencyZone[]>>(
    EP.Platform_Residency_Zones_Url,
  );
  return unwrap<ResidencyZone[]>(data) ?? [];
}

export async function listSigningKeys(): Promise<SigningKeyRow[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<SigningKeyRow[]>>(
    EP.Platform_Admin_Signing_Keys_Url,
  );
  return unwrap<SigningKeyRow[]>(data) ?? [];
}

export async function listRlsBypass(limit = 200): Promise<RlsBypassRow[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<RlsBypassRow[]>>(
    `${EP.Platform_Admin_Rls_Bypass_Url}?limit=${limit}`,
  );
  return unwrap<RlsBypassRow[]>(data) ?? [];
}

export async function listRlsBypassSites(): Promise<RlsBypassSiteCount[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<RlsBypassSiteCount[]>>(
    EP.Platform_Admin_Rls_Bypass_Sites_Url,
  );
  return unwrap<RlsBypassSiteCount[]>(data) ?? [];
}

export async function listRealmMismatches(sinceHours = 24): Promise<RealmMismatchRow[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<RealmMismatchRow[]>>(
    `${EP.Platform_Admin_Realm_Mismatch_Url}?since_hours=${sinceHours}`,
  );
  return unwrap<RealmMismatchRow[]>(data) ?? [];
}

export async function listAppAudiences(): Promise<AppAudienceRow[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<AppAudienceRow[]>>(
    EP.Platform_Admin_Audiences_Url,
  );
  return unwrap<AppAudienceRow[]>(data) ?? [];
}

export async function listCustomerActivityAcrossTenants(): Promise<CustomerActivityPeriodRow[]> {
  const { data } = await axiosInstance.get<ApiEnvelope<CustomerActivityPeriodRow[]>>(
    EP.Platform_Admin_Customer_Activity_Url,
  );
  return unwrap<CustomerActivityPeriodRow[]>(data) ?? [];
}

export async function getCustomerActivity(orgId: string): Promise<CustomerActivityPeriodRow> {
  const { data } = await axiosInstance.get<ApiEnvelope<CustomerActivityPeriodRow>>(
    EP.Platform_Org_Customer_Activity_Url(orgId),
  );
  return unwrap<CustomerActivityPeriodRow>(data);
}

export async function listCustomerAccess(orgId: string): Promise<CustomerAccessRow[]> {
  return (await listCustomerAccessWithMeta(orgId)).rows;
}

/**
 * The access list, WITH the server's reason when it is unavailable.
 *
 * Platform holds no customer identifier and cannot enumerate customer subjects
 * — that absence is the property keeping customers off the seat count, not a
 * gap. So the endpoint answers with an empty list and a reason, and the screen
 * needs the reason: "no customers have signed in" and "this console cannot see
 * the list" are different facts, and on an access surface they matter
 * differently.
 */
export async function listCustomerAccessWithMeta(
  orgId: string,
): Promise<{ rows: CustomerAccessRow[]; reason: string | null }> {
  const { data } = await axiosInstance.get<
    ApiEnvelope<CustomerAccessRow[]> & { meta?: { available?: boolean; reason?: string } }
  >(`${EP.Platform_Org_Customer_Access_Url(orgId)}?sort=last_seen`);
  const rows = unwrap<CustomerAccessRow[]>(data) ?? [];
  const meta = (data as { meta?: { available?: boolean; reason?: string } }).meta;
  return { rows, reason: meta?.available === false ? (meta.reason ?? 'unavailable') : null };
}

/**
 * Bulk revoke. Revoking access does NOT delete the customer's records and does
 * not affect their access at any other business — the confirmation dialog says
 * both, and it says them because an operator revoking 900 dormant grants must
 * be certain they have not destroyed 900 booking histories.
 */
export async function revokeCustomerAccess(orgId: string, ids: string[]): Promise<{ revoked: number }> {
  const { data } = await axiosInstance.post<ApiEnvelope<{ revoked: number }>>(
    EP.Platform_Org_Customer_Access_Revoke_Url(orgId),
    { customer_ids: ids },
  );
  return unwrap<{ revoked: number }>(data);
}
